import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import Select from "react-select";

import { FLOW_TYPES } from "../../constants/flowType";
import { useTicketingContext } from "../../store";
import { HELPERS } from "../../utils";
import { VISUAL_HELPERS } from "../../utils/visualHelpers";

const TimeslotSelect = ({
  allowedAmount,
  onTimeSlotSelect,
  timeslots,
  defaultValue = null,
  isLastElement = false,
  addonId,
}) => {
  const { state } = useTicketingContext();
  const { texts, flowType } = state;
  const [selectValue, setSelectValue] = useState(null);
  const isSmallScreen = useMediaQuery({ query: "(max-width: 1366px)" });

  const onSelectChangeHandler = (selectedOpt) => {
    if (selectedOpt.isDisabled || selectedOpt.ticketsLeft <= 0) {
      return;
    }
    setSelectValue(selectedOpt);
    onTimeSlotSelect(selectedOpt);
  };

  const options = timeslots
    .map((timeslot) => ({
      value: timeslot.timestamp_in_milis,
      entry_no: timeslot.entry_no,
      timeString:
        flowType === FLOW_TYPES.ACTIVITIES_ONLY
          ? VISUAL_HELPERS.displayDateTimeString(timeslot.timestamp_in_milis)
          : VISUAL_HELPERS.displayTimeString(timeslot.timestamp_in_milis),
      ticketsLeft: timeslot.tickets_left,
      isClosed: timeslot.active !== "true",
      isTimeslotTaken: HELPERS.checkIsTimeslotTaken(
        timeslot,
        state.selectedTimeslots,
        addonId,
        flowType === FLOW_TYPES.ACTIVITIES_ONLY
      ),
      isDisabled:
        timeslot.active !== "true" ||
        allowedAmount > timeslot.tickets_left ||
        HELPERS.checkIsTimeslotTaken(
          timeslot,
          state.selectedTimeslots,
          addonId,
          flowType === FLOW_TYPES.ACTIVITIES_ONLY
        ),
    }))
    .sort((a, b) => (a.value > b.value ? 1 : -1));
  // Push "Select timeslot" option
  // options.unshift({ value: -1 });

  const defaultValueIndex = () => {
    let index;
    if (defaultValue) {
      index = options.findIndex(
        (opt) => parseInt(opt.value) === parseInt(defaultValue)
      );
    }
    return Math.max(0, index);
  };

  const getOptionLabelHandler = (option) => {
    if (option.isTimeslotTaken) {
      return (
        <>
          <span className="option-display-timeslot disabled">
            {option.timeString}
          </span>
          <span className="option-display-qty-info disabled">
            (
            {texts.common?.timeslot_overlaps ||
              "Overlaps with exhibition timeslot"}
            )
          </span>
        </>
      );
    }
    if (option.isClosed) {
      return (
        <>
          <span className="option-display-timeslot disabled">
            {option.timeString}
          </span>
          <span className="option-display-qty-info disabled">
            ({texts.common?.closed || "Closed"})
          </span>
        </>
      );
    }
    if (option.ticketsLeft === 0) {
      return (
        <>
          <span className="option-display-timeslot disabled">
            {option.timeString}
          </span>
          <span className="option-display-qty-info disabled">
            ({texts.common?.sold_out || "Sold out"})
          </span>
        </>
      );
    }
    if (option.ticketsLeft === 1) {
      return (
        <>
          <span
            className={`option-display-timeslot ${
              option.ticketsLeft < allowedAmount ? "disabled" : ""
            }`}
          >
            {option.timeString}
          </span>
          <span
            className={`option-display-qty-info ${
              option.ticketsLeft < allowedAmount ? "disabled" : ""
            }`}
          >{` (${option.ticketsLeft} ${
            texts?.steps?.[2]?.timeslot_capacity_singular || "Ticket left"
          })`}</span>
        </>
      );
    }
    if (option.ticketsLeft > 1) {
      return (
        <>
          <span
            className={`option-display-timeslot ${
              option.ticketsLeft < allowedAmount ? "disabled" : ""
            }`}
          >
            {option.timeString}
          </span>
          <span
            className={`option-display-qty-info ${
              option.ticketsLeft < allowedAmount ? "disabled" : ""
            }`}
          >{` (${option.ticketsLeft} ${
            texts?.steps?.[2]?.timeslot_capacity || "Tickets left"
          })`}</span>
        </>
      );
    }
  };

  const findMatchingItem = (object, array) => {
    for (const key in object) {
      if (object.hasOwnProperty(key)) {
        const entryNo = object[key].entry_no;
        const matchingItem = array.find((item) => item.entry_no === entryNo);
        if (matchingItem) {
          return matchingItem;
        }
      }
    }
    return null;
  };
  const isSelectDisabled =
    options.length === 0 ||
    options.filter((option) => option.ticketsLeft > 0 && !option.isDisabled)
      .length === 0;

  useEffect(() => {
    if (
      selectValue &&
      options.find((option) => option.value === selectValue.value)
        .isTimeslotTaken &&
      !state.selectedTimeslots.find(
        (timeslot) => timeslot?.entry_no === selectValue.entry_no
      )
    ) {
      setSelectValue(null);
    }
    if (
      options.length > 0 &&
      !selectValue &&
      timeslots.length > 0 &&
      state.selectedTimeslots.length > 1 &&
      Object.keys(state.summary.selectedAddonTickets).length > 0 &&
      state.selectedTimeslots.find((timeslot) => timeslot.addonId === addonId)
    ) {
      const matchingTimeslot = findMatchingItem(
        state.summary.selectedAddonTickets,
        timeslots
      );
      if (matchingTimeslot && matchingTimeslot?.active === "true") {
        setSelectValue({
          value: matchingTimeslot.timestamp_in_milis,
          entry_no: matchingTimeslot.entry_no,
          timeString:
            flowType === FLOW_TYPES.ACTIVITIES_ONLY
              ? VISUAL_HELPERS.displayDateTimeString(
                  matchingTimeslot.timestamp_in_milis
                )
              : VISUAL_HELPERS.displayTimeString(
                  matchingTimeslot.timestamp_in_milis
                ),
          ticketsLeft: matchingTimeslot.tickets_left,
          isClosed: matchingTimeslot.active !== "true",
          isTimeslotTaken: HELPERS.checkIsTimeslotTaken(
            matchingTimeslot,
            state.selectedTimeslots,
            addonId,
            flowType === FLOW_TYPES.ACTIVITIES_ONLY
          ),
          isDisabled:
            matchingTimeslot.active !== "true" ||
            allowedAmount > matchingTimeslot.tickets_left,
        });
      }
    }
  }, [isSelectDisabled, options]);

  return (
    <Select
      blurInputOnSelect={true}
      className="timeslot-select-container"
      classNamePrefix="timeslot-select"
      defaultValue={options[defaultValueIndex()]}
      getOptionLabel={getOptionLabelHandler}
      getOptionValue={(option) => option.value}
      isDisabled={isSelectDisabled}
      isSearchable={false}
      maxMenuHeight={isSmallScreen ? 265 : 9999}
      menuPlacement={isLastElement ? "top" : "bottom"}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      onChange={onSelectChangeHandler}
      options={options}
      placeholder={
        isSelectDisabled
          ? texts?.steps?.[2]?.timeslot_no_available || "No available timeslots"
          : texts?.steps?.[2]?.select_timeslot || "Select timeslot"
      }
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      value={selectValue}
    />
  );
};

export default TimeslotSelect;
